import { Route, createBrowserRouter, createRoutesFromElements, RouterProvider, Outlet, Navigate } from "react-router-dom";
import "./css/main.scss"

// styled-components setup
import { ThemeProvider } from '@emotion/react'
import { theme, muiTheme } from "./utils/theme";

// mui setup
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

// snackbar
import SnackbarComponent from "./store/snackbar/Snackbar";

// permission based route
import RequirePermission from "./router/PermittedRoute";

// for app updates on pwa
import UpdatePrompt from './UpdatePrompt';

// routes
import Layout from './components/Layout';
import Signin from './pages/login/Signin';
import Dashboard from "./pages/Dashboard";
import Appointments from "./pages/Appointments";
import Calendar from "./pages/Calendar";
import CalendarFull from "./pages/CalendarFull";
import VendorPortal from "./pages/VendorPortal";
import Schedule from "./pages/Schedule";
// import InventoryList from "./components/Inventory/InventoryList";
import Inventory from "./pages/Inventory";
import Customers from "./pages/Customers";
import Vendors from "./pages/Vendors";
import Stylists from "./pages/Stylists";
import Specialists from "./pages/Specialists";
import Services from "./pages/Services";
import Service_Tickets from "./pages/Service_Tickets";
import Users from "./pages/Users";
import Roles from "./pages/Roles";
import Shipments from "./pages/Shipments";
import Requests from "./pages/Requests";
import Settings from "./pages/Settings";
import Checkout from "./pages/Checkout";
import Payments from "./pages/Payments";
import Invoices from "./pages/Invoices";
import Repairs from "./pages/Repairs";
import Repair_Tickets from "./pages/Repair_Tickets";
import Adjustments from "./pages/Adjustments";
import Bills from "./pages/Bills";
import VendorPayments from "./pages/Vendor_Payments";
import VendorBalances from "./pages/Vendor_Balances";
import Messages from "./pages/Messages";
import Products from "./pages/Products";
import WigOrders from "./pages/WigOrders";
import Reports from "./pages/Reports";
import Promotions from "./pages/Promotions";
import SyncedShipments from "./pages/Synced_Shipments";
import ContactGroups from "./pages/Contact_Groups";



import NotFoundPage from './pages/NotFoundPage';
import UnauthorizedPage from "./pages/Unauthorized";
import ErrorPage from "./pages/ErrorPage";

// ProtectedRoute
import ProtectedRoute from './router/ProtectedRoute';


const Root = () => {
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

const routes = createRoutesFromElements(
  <>
    <Route path="/setup-account/:user_id/" element={<Signin />} />
    <Route path="/login/*" element={<Signin />} />
      
      {/* Main application with auth check */}
      <Route path="/" element={
        <ProtectedRoute>
          <Root />
        </ProtectedRoute>
      }>
        {/* Pathless wrapper: errorElement renders inside Layout's Outlet */}
        <Route errorElement={<ErrorPage />}></Route>
        
        {/* Regular route for unauthorized page */}
        <Route path="unauthorized/*" element={<UnauthorizedPage />} />
        
        {/* Permission protected routes with specific resources */}
        <Route 
          path="dashboard/*" 
          element={<RequirePermission element={Dashboard} resource="Dashboard" />}
        />
        <Route 
          path="appointments/*" 
          element={<RequirePermission element={Appointments} resource="Appointment" />}
        />
        <Route 
          path="calendar/*" 
          element={<RequirePermission element={Calendar} resource="Calendar" />}
        />
        <Route 
          path="calendar-full/*" 
          element={<RequirePermission element={CalendarFull} resource="Calendar" />}
        />
        <Route 
          path="schedule/*" 
          element={<RequirePermission element={Schedule} resource="Schedule" />}
        />
        <Route 
          path="appointments/*" 
          element={<RequirePermission element={Dashboard} resource="Appointment" />}
        />
        <Route 
          path="customers/*" 
          element={<RequirePermission element={Customers} resource="Customer" />}
        />
        <Route 
          path="contact_groups/*" 
          element={<RequirePermission element={ContactGroups} resource="Customer" />}
        />
        <Route
          path="reports/*"
          element={<RequirePermission element={Reports} resource="Report" />}
        />
        <Route
          path="wig_orders/*"
          element={<RequirePermission element={WigOrders} resource="WigOrder" />}
        />
        <Route
          path="messages/*"
          element={<RequirePermission element={Messages} resource="Message" />}
        />
        <Route
          path="promotions/*"
          element={<RequirePermission element={Promotions} resource="Message" />}
        />
        <Route 
          path="vendors/*" 
          element={<RequirePermission element={Vendors} resource="Vendor" />}
        />
        <Route 
          path="stylists/*" 
          element={<RequirePermission element={Stylists} resource="Stylist" />}
        />
        <Route 
          path="specialists/*" 
          element={<RequirePermission element={Specialists} resource="Specialist" />}
        />
        <Route 
          path="inventory/*" 
          element={<RequirePermission element={Inventory} resource="Inventory" />}
        />
        <Route
          path="products/*"
          element={<RequirePermission element={Products} resource="Product" />}
        />
        <Route 
          path="sales/*" 
          element={<RequirePermission element={Dashboard} resource="Sales" />}
        />
        <Route 
          path="payments/*" 
          element={<RequirePermission element={Payments} resource="Payment" />}
        />
        <Route 
          path="checkout/*" 
          element={<RequirePermission element={Checkout} resource="Checkout" />}
        />
        <Route 
          path="invoices/*" 
          element={<RequirePermission element={Invoices} resource="Invoice" />}
        />
        <Route 
          path="shipments/*" 
          element={<RequirePermission element={Shipments} resource="Shipment" />}
        />
        <Route 
          path="synced_shipments/*" 
          element={<RequirePermission element={SyncedShipments} resource="Shipment" />}
        />
        <Route 
          path="requests/*" 
          element={<RequirePermission element={Requests} resource="Request" />}
        />
        <Route 
          path="bills/*" 
          element={<RequirePermission element={Bills} resource="Bill" />}
        />
        <Route 
          path="vendor_balances/*" 
          element={<RequirePermission element={VendorBalances} resource="Bill" />}
        />
        <Route 
          path="vendor_payments/*" 
          element={<RequirePermission element={VendorPayments} resource="VendorPayment" />}
        />
        <Route 
          path="services/*" 
          element={<RequirePermission element={Services} resource="Service" />}
        />
        <Route 
          path="service_tickets/*" 
          element={<RequirePermission element={Service_Tickets} resource="ServiceTicket" />}
        />
        <Route 
          path="repairs/*" 
          element={<RequirePermission element={Repairs} resource="Repair" />}
        />
        <Route 
          path="repair_tickets/*" 
          element={<RequirePermission element={Repair_Tickets} resource="RepairTicket" />}
        />
        <Route 
          path="adjustments/*" 
          element={<RequirePermission element={Adjustments} resource="Adjustment" />}
        />
        <Route 
          path="settings/*" 
          element={<RequirePermission element={Settings} resource="Settings" />}
        />
        <Route 
          path="users/*" 
          element={<RequirePermission element={Users} resource="Users" action="view" />}
        />
        <Route 
          path="roles/*" 
          element={<RequirePermission element={Roles} resource="Roles" />}
        />
        <Route 
          path="vendor-portal/*" 
          element={<RequirePermission element={VendorPortal} resource="VendorPortal" />}
        />
        <Route 
          path="/*" 
          element={<NotFoundPage />} 
        />

        <Route index element={<Navigate to="/calendar" replace />} />
      </Route>
  </>
)

const router = createBrowserRouter(routes)

function App() {
  // console.log(muiTheme)
  return (
   <ThemeProvider theme={theme}>
    <MuiThemeProvider theme={muiTheme}>
      <SnackbarComponent />
      <UpdatePrompt />
      <RouterProvider router={router} />
    </MuiThemeProvider>
   </ThemeProvider>
  )
}

export default App;
